import { useState, useEffect, useCallback } from "react";
import { IMG } from "@/data/site";
import { Phone, CalendarCheck, Navigation, Eye, LogOut, RefreshCw, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "s8_admin_token";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "all", label: "All Time" },
];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bone px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md border-2 border-charcoal bg-paper p-8">
        <div className="flex items-center gap-3">
          <img src={IMG.logo} alt="Super 8 Ottawa" className="h-12 w-auto" />
          <div>
            <h1 className="font-display text-2xl uppercase leading-none text-charcoal">Admin</h1>
            <p className="font-body text-xs uppercase tracking-widest text-fog">Analytics Dashboard</p>
          </div>
        </div>
        <div className="mt-8 space-y-4">
          <div>
            <label className="font-body text-xs font-semibold uppercase tracking-wide text-charcoal/70">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="mt-2 w-full border border-charcoal/25 bg-white px-4 py-3 font-body text-charcoal outline-none focus:border-brand-red"
              required
            />
          </div>
          <div>
            <label className="font-body text-xs font-semibold uppercase tracking-wide text-charcoal/70">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="mt-2 w-full border border-charcoal/25 bg-white px-4 py-3 font-body text-charcoal outline-none focus:border-brand-red"
              required
            />
          </div>
          {error && <p data-testid="admin-login-error" className="font-body text-sm text-brand-red">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-brand-red py-3 font-heading font-black uppercase text-white transition-colors hover:bg-brand-yellow hover:text-black disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="border-2 border-charcoal bg-paper p-6">
    <Icon className="h-6 w-6 text-brand-red" />
    <div className="mt-4 font-display text-4xl leading-none text-charcoal">{value}</div>
    <div className="mt-2 font-body text-xs uppercase tracking-wide text-fog">{label}</div>
  </div>
);

const Dashboard = ({ token, onLogout }) => {
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${API}/api/admin/summary?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const d = await res.json();
      setData(d);
    } catch (e) {
      setErr("Could not load data");
    } finally {
      setLoading(false);
    }
  }, [range, token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals || {};
  const maxDay = Math.max(1, ...(data?.timeseries || []).map((d) => d.count));
  const maxPage = Math.max(1, ...(data?.top_pages || []).map((p) => p.views));

  return (
    <div className="min-h-screen bg-bone pb-20">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b-2 border-charcoal bg-paper px-5 py-4 md:px-10">
        <div className="flex items-center gap-3">
          <img src={IMG.logo} alt="Super 8 Ottawa" className="h-10 w-auto" />
          <div>
            <h1 className="font-display text-xl uppercase leading-none text-charcoal">Analytics</h1>
            <p className="font-body text-[10px] uppercase tracking-widest text-fog">Super 8 Ottawa</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 border border-charcoal/25 px-3 py-2 font-body text-xs uppercase text-charcoal hover:border-brand-red">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 border border-charcoal/25 px-3 py-2 font-body text-xs uppercase text-charcoal hover:border-brand-red">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-5 pt-8 md:px-10">
        <div className="flex flex-wrap gap-2" data-testid="range-selector">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key}`}
              className={`border px-4 py-2 font-body text-xs font-semibold uppercase tracking-wide transition-colors ${
                range === r.key ? "border-brand-red bg-brand-red text-white" : "border-charcoal/25 text-charcoal/70 hover:border-brand-red"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {loading && <p className="mt-10 font-body text-fog">Loading…</p>}
        {err && <p className="mt-10 font-body text-brand-red">{err}</p>}

        {data && !loading && (
          <div data-testid="admin-dashboard" className="mt-8 space-y-10">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
              <KPI icon={Eye} label="Page Views" value={t.page_views ?? 0} testid="kpi-page_views" />
              <KPI icon={CalendarCheck} label="Book Clicks" value={t.book ?? 0} testid="kpi-book" />
              <KPI icon={Phone} label="Call Clicks" value={t.call ?? 0} testid="kpi-call" />
              <KPI icon={Navigation} label="Directions" value={t.directions ?? 0} testid="kpi-directions" />
              <KPI icon={CalendarCheck} label="Check Avail." value={t.check_availability ?? 0} testid="kpi-check" />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="border-2 border-charcoal bg-paper p-6">
                <h2 className="font-heading text-lg font-black text-charcoal">Most viewed pages</h2>
                <p className="mt-1 font-body text-xs text-fog">What guests are most interested in</p>
                <div className="mt-6 space-y-3">
                  {(data.top_pages || []).length === 0 && <p className="font-body text-sm text-fog">No data yet.</p>}
                  {(data.top_pages || []).map((p) => (
                    <div key={p.path}>
                      <div className="flex items-center justify-between font-body text-sm">
                        <span className="text-charcoal">{p.label}</span>
                        <span className="font-semibold text-charcoal">{p.views}</span>
                      </div>
                      <div className="mt-1 h-2 w-full bg-sand">
                        <div className="h-2 bg-brand-yellow" style={{ width: `${(p.views / maxPage) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-2 border-charcoal bg-paper p-6">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-brand-red" />
                  <h2 className="font-heading text-lg font-black text-charcoal">Daily activity</h2>
                </div>
                <p className="mt-1 font-body text-xs text-fog">Total events per day ({data.total_events} total)</p>
                <div className="mt-6 space-y-2">
                  {(data.timeseries || []).length === 0 && <p className="font-body text-sm text-fog">No data yet.</p>}
                  {(data.timeseries || []).slice(-14).map((d) => (
                    <div key={d.day} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 font-body text-xs text-fog">{d.day}</span>
                      <div className="h-3 flex-1 bg-sand">
                        <div className="h-3 bg-brand-red" style={{ width: `${(d.count / maxDay) * 100}%` }} />
                      </div>
                      <span className="w-8 text-right font-body text-xs text-charcoal">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };
  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
